/**
 * Style guide 2.0
 *
 * Drives the mobile carousel only; the desktop table is plain markup. The track is a native scroll-snap scroller, so
 * swiping works without JS. This script adds the arrows (which wrap around at the ends), keeps the dots in sync with
 * the scroll position, and brings a style into view when its block is selected in the theme editor.
 *
 * Autoplay: when the section sets `autoplay` (the pause in ms), the carousel moves on by itself, but only while it is
 * actually visible (mobile layout, on screen, tab in front). The first time the visitor touches, clicks, swipes or uses
 * the keyboard on the block, autoplay stops for good, so it never fights the visitor.
 */
(function () {
  if (customElements.get('style-guide')) return;

  const INTERACTION_EVENTS = ['pointerdown', 'touchstart', 'wheel', 'keydown'];

  class StyleGuide extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-style-guide-track]');
      if (!this.track || this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';

      this.slides = Array.from(this.querySelectorAll('[data-style-guide-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-style-guide-dot]'));
      this.index = 0;

      this.querySelector('[data-style-guide-prev]')?.addEventListener('click', () => this.go(this.index - 1));
      this.querySelector('[data-style-guide-next]')?.addEventListener('click', () => this.go(this.index + 1));
      this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.go(i)));

      this.track.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') { event.preventDefault(); this.go(this.index - 1); }
        if (event.key === 'ArrowRight') { event.preventDefault(); this.go(this.index + 1); }
      });

      this.onScroll = this.onScroll.bind(this);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });

      this.onBlockSelect = this.onBlockSelect.bind(this);
      document.addEventListener('shopify:block:select', this.onBlockSelect);

      this.setActive(0);
      this.setupAutoplay();
    }

    disconnectedCallback() {
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      this.stopAutoplay();
    }

    go(index) {
      const count = this.slides.length;
      if (!count) return;
      const target = ((index % count) + count) % count;
      this.track.scrollTo({ left: target * this.track.clientWidth, behavior: this.scrollBehavior() });
      this.setActive(target);
    }

    onScroll() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        const width = this.track.clientWidth || 1;
        this.setActive(Math.round(this.track.scrollLeft / width));
      });
    }

    setActive(index) {
      const target = Math.min(Math.max(index, 0), this.slides.length - 1);
      this.index = target;
      this.dots.forEach((dot, i) => dot.setAttribute('aria-current', i === target ? 'true' : 'false'));
    }

    onBlockSelect(event) {
      const i = this.slides.findIndex((slide) => slide.dataset.blockId === event.detail.blockId);
      if (i === -1) return;
      this.stopAutoplay();
      this.go(i);
    }

    scrollBehavior() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    }

    /* ---------- Autoplay ---------- */

    setupAutoplay() {
      this.autoplayDelay = Number(this.getAttribute('autoplay')) || 0;
      if (!this.autoplayDelay || this.slides.length < 2) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      this.autoplayEnabled = true;
      this.isInView = false;

      this.onInteraction = this.stopAutoplay.bind(this);
      INTERACTION_EVENTS.forEach((type) => this.addEventListener(type, this.onInteraction, { passive: true }));

      this.onVisibilityChange = this.updateAutoplay.bind(this);
      document.addEventListener('visibilitychange', this.onVisibilityChange);

      /* The carousel is display:none on desktop, so a resize can switch autoplay on or off. */
      this.mobileQuery = window.matchMedia('(max-width: 999px)');
      this.mobileQuery.addEventListener?.('change', this.onVisibilityChange);

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          this.isInView = entries[0].isIntersecting;
          this.updateAutoplay();
        }, { threshold: 0.5 });
        this.observer.observe(this.track);
      } else {
        this.isInView = true;
        this.updateAutoplay();
      }
    }

    /** Runs the timer only while autoplay is allowed and the visitor can actually see the carousel. */
    updateAutoplay() {
      const shouldRun = this.autoplayEnabled
        && this.isInView
        && document.visibilityState === 'visible'
        && this.mobileQuery.matches;

      if (shouldRun && !this.autoplayTimer) {
        this.autoplayTimer = setInterval(() => this.go(this.index + 1), this.autoplayDelay);
      } else if (!shouldRun && this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }

    /** Stops autoplay permanently for this page view. */
    stopAutoplay() {
      if (!this.autoplayEnabled) return;
      this.autoplayEnabled = false;

      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;

      INTERACTION_EVENTS.forEach((type) => this.removeEventListener(type, this.onInteraction));
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      this.mobileQuery?.removeEventListener?.('change', this.onVisibilityChange);
      this.observer?.disconnect();
    }
  }

  customElements.define('style-guide', StyleGuide);
})();
